import type { ImgHTMLAttributes } from "react";
import { PUBLIC_IMAGE_ORIGINALS } from "../lib/responsiveImageManifest";

const RESPONSIVE_WIDTHS = [320, 640, 1024] as const;

export type ResponsiveImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet"
> & {
  src: string;
};

const imageBasename = (src: string) => {
  const path = src.split(/[?#]/, 1)[0];
  const filename = path.slice(path.lastIndexOf("/") + 1);
  return filename.replace(/\.[^.]+$/, "");
};

const imageExtension = (src: string) => {
  const path = src.split(/[?#]/, 1)[0];
  const filename = path.slice(path.lastIndexOf("/") + 1);
  return filename.slice(filename.lastIndexOf(".")).toLowerCase();
};

const extensionPriority = (src: string) => {
  switch (imageExtension(src)) {
    case ".jpg":
      return 0;
    case ".jpeg":
      return 1;
    case ".png":
      return 2;
    default:
      return 3;
  }
};

const imagePathWithoutQuery = (src: string) => src.split(/[?#]/, 1)[0];

const knownOriginals = new Set<string>(PUBLIC_IMAGE_ORIGINALS);
const preferredOriginalByBasename = new Map<string, string>();

for (const original of PUBLIC_IMAGE_ORIGINALS) {
  const basename = imageBasename(original);
  const current = preferredOriginalByBasename.get(basename);
  if (!current || extensionPriority(original) < extensionPriority(current)) {
    preferredOriginalByBasename.set(basename, original);
  }
}

const withBaseUrl = (path: string) => {
  const baseUrl = import.meta.env.BASE_URL || "/";
  if (baseUrl === "/") {
    return path;
  }

  return `${baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
};

const getResponsiveBasename = (src: string) => {
  const sourcePath = imagePathWithoutQuery(src);
  if (!knownOriginals.has(sourcePath)) {
    return undefined;
  }

  const basename = imageBasename(sourcePath);
  return preferredOriginalByBasename.has(basename) ? basename : undefined;
};

/**
 * Renders a public image with generated WebP candidates when its original is
 * present in the responsive-image manifest. The original URL remains the
 * fallback so this component is also safe for external and future images.
 */
export function ResponsiveImage({
  src,
  alt,
  width,
  height,
  className,
  loading,
  fetchPriority,
  decoding = "async",
  sizes,
  ...imageProps
}: ResponsiveImageProps) {
  const basename = getResponsiveBasename(src);

  if (!basename) {
    return (
      <img
        {...imageProps}
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding={decoding}
        sizes={sizes}
      />
    );
  }

  const srcSet = RESPONSIVE_WIDTHS.map(
    (responsiveWidth) =>
      `${withBaseUrl(`/images/responsive/${basename}-${responsiveWidth}.webp`)} ${responsiveWidth}w`,
  ).join(", ");

  return (
    <picture style={{ display: "contents" }}>
      <source type="image/webp" srcSet={srcSet} sizes={sizes} />
      <img
        {...imageProps}
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding={decoding}
        sizes={sizes}
      />
    </picture>
  );
}

export default ResponsiveImage;